/* eslint react/no-array-index-key: 0 */

import React from 'react';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import {
  forbidExtraProps,
  mutuallyExclusiveProps,
  nonNegativeInteger,
} from 'airbnb-prop-types';
import { css, withStyles, withStylesPropTypes } from 'react-with-styles';
import moment from 'moment';

import { CalendarDayPhrases } from 'react-dates/src/defaultPhrases';
import getPhrasePropTypes from 'react-dates/src/utils/getPhrasePropTypes';

import CalendarWeek from './CalendarWeek';
import CalendarDay from './CalendarDay';

import calculateDimension from 'react-dates/src/utils/calculateDimension';
import getCalendarMonthWeeks from 'react-dates/src/utils/getCalendarMonthWeeks';
import isSameDay from 'react-dates/src/utils/isSameDay';
import toISODateString from 'react-dates/src/utils/toISODateString';

import ModifiersShape from 'react-dates/src/shapes/ModifiersShape';
import ScrollableOrientationShape from 'react-dates/src/shapes/ScrollableOrientationShape';
import DayOfWeekShape from 'react-dates/src/shapes/DayOfWeekShape';
import { connect } from 'react-redux';

import {
  HORIZONTAL_ORIENTATION,
  VERTICAL_SCROLLABLE,
  DAY_SIZE,
} from 'react-dates/src/constants';

const propTypes = forbidExtraProps({
  ...withStylesPropTypes,
  month: momentPropTypes.momentObj,
  horizontalMonthPadding: nonNegativeInteger,
  isVisible: PropTypes.bool,
  enableOutsideDays: PropTypes.bool,
  modifiers: PropTypes.objectOf(ModifiersShape),
  orientation: ScrollableOrientationShape,
  daySize: nonNegativeInteger,
  onDayClick: PropTypes.func,
  onDayMouseEnter: PropTypes.func,
  onDayMouseLeave: PropTypes.func,
  onMonthSelect: PropTypes.func,
  onYearSelect: PropTypes.func,
  renderMonthText: mutuallyExclusiveProps(
    PropTypes.func,
    'renderMonthText',
    'renderMonthElement',
  ),
  renderCalendarDay: PropTypes.func,
  renderDayContents: PropTypes.func,
  renderMonthElement: mutuallyExclusiveProps(
    PropTypes.func,
    'renderMonthText',
    'renderMonthElement',
  ),
  firstDayOfWeek: DayOfWeekShape,
  setMonthTitleHeight: PropTypes.func,
  verticalBorderSpacing: nonNegativeInteger,

  focusedDate: momentPropTypes.momentObj, // indicates focusable day
  isFocused: PropTypes.bool, // indicates whether or not to move focus to focusable day

  // i18n
  monthFormat: PropTypes.string,
  phrases: PropTypes.shape(getPhrasePropTypes(CalendarDayPhrases)),
  dayAriaLabelFormat: PropTypes.string,
  i18n: PropTypes.object,
});

const defaultProps = {
  month: moment(),
  horizontalMonthPadding: 13,
  isVisible: true,
  enableOutsideDays: false,
  modifiers: {},
  orientation: HORIZONTAL_ORIENTATION,
  daySize: DAY_SIZE,
  onDayClick() {},
  onDayMouseEnter() {},
  onDayMouseLeave() {},
  onMonthSelect() {},
  onYearSelect() {},
  renderMonthText: null,
  renderCalendarDay: props => <CalendarDay {...props} />,
  renderDayContents: null,
  renderMonthElement: null,
  firstDayOfWeek: null,
  setMonthTitleHeight: null,

  focusedDate: null,
  isFocused: false,

  // i18n
  monthFormat: 'MMMM YYYY', // english locale
  phrases: CalendarDayPhrases,
  dayAriaLabelFormat: undefined,
  verticalBorderSpacing: undefined,
  i18n: null,
};

class CalendarMonth extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      weeks: getCalendarMonthWeeks(
        props.month,
        props.enableOutsideDays,
        props.firstDayOfWeek == null
          ? moment.localeData().firstDayOfWeek()
          : props.firstDayOfWeek,
      ),
    };

    this.setCaptionRef = this.setCaptionRef.bind(this);
    this.setMonthTitleHeight = this.setMonthTitleHeight.bind(this);
  }

  componentDidMount() {
    this.setMonthTitleHeightTimeout = setTimeout(this.setMonthTitleHeight, 0);
  }

  componentWillReceiveProps(nextProps) {
    const { month, enableOutsideDays, firstDayOfWeek } = nextProps;
    const {
      month: prevMonth,
      enableOutsideDays: prevEnableOutsideDays,
      firstDayOfWeek: prevFirstDayOfWeek,
    } = this.props;
    if (
      !month.isSame(prevMonth) ||
      enableOutsideDays !== prevEnableOutsideDays ||
      firstDayOfWeek !== prevFirstDayOfWeek
    ) {
      this.setState({
        weeks: getCalendarMonthWeeks(
          month,
          enableOutsideDays,
          firstDayOfWeek == null
            ? moment.localeData().firstDayOfWeek()
            : firstDayOfWeek,
        ),
      });
    }
  }

  componentWillUnmount() {
    if (this.setMonthTitleHeightTimeout) {
      clearTimeout(this.setMonthTitleHeightTimeout);
    }
  }

  setMonthTitleHeight() {
    const { setMonthTitleHeight } = this.props;
    if (setMonthTitleHeight) {
      const captionHeight = calculateDimension(
        this.captionRef,
        'height',
        true,
        true,
      );
      setMonthTitleHeight(captionHeight);
    }
  }

  setCaptionRef(ref) {
    this.captionRef = ref;
  }

  render() {
    const {
      dayAriaLabelFormat,
      daySize,
      focusedDate,
      horizontalMonthPadding,
      isFocused,
      isVisible,
      modifiers,
      month,
      monthFormat,
      onDayClick,
      onDayMouseEnter,
      onDayMouseLeave,
      onMonthSelect,
      onYearSelect,
      orientation,
      phrases,
      renderCalendarDay,
      renderDayContents,
      renderMonthElement,
      renderMonthText,
      styles,
      verticalBorderSpacing,
      i18n,
    } = this.props;

    const { weeks } = this.state;
    const monthTitle = renderMonthText
      ? renderMonthText(month)
      : month
          .locale(i18n.locale === 'en' ? i18n.locale : 'vi-VN')
          .format(monthFormat);

    const verticalScrollable = orientation === VERTICAL_SCROLLABLE;

    return (
      <div
        {...css(styles.CalendarMonth, {
          padding: `0 ${horizontalMonthPadding}px`,
        })}
        data-visible={isVisible}
      >
        <div
          ref={this.setCaptionRef}
          {...css(
            styles.CalendarMonth_caption,
            verticalScrollable &&
              styles.CalendarMonth_caption__verticalScrollable,
          )}
        >
          {renderMonthElement ? (
            renderMonthElement({
              month,
              onMonthSelect,
              onYearSelect,
              isVisible,
            })
          ) : (
            <strong>{monthTitle}</strong>
          )}
        </div>

        <table
          {...css(
            !verticalBorderSpacing && styles.CalendarMonth_table,
            verticalBorderSpacing && styles.CalendarMonth_verticalSpacing,
            verticalBorderSpacing && {
              borderSpacing: `0px ${verticalBorderSpacing}px`,
            },
          )}
          role="presentation"
        >
          <tbody>
            {weeks.map((week, i) => (
              <CalendarWeek key={i}>
                {week.map((day, dayOfWeek) =>
                  renderCalendarDay({
                    key: dayOfWeek,
                    day,
                    daySize,
                    isOutsideDay: !day || day.month() !== month.month(),
                    tabIndex: isVisible && isSameDay(day, focusedDate) ? 0 : -1,
                    isFocused,
                    onDayMouseEnter,
                    onDayMouseLeave,
                    onDayClick,
                    renderDayContents,
                    phrases,
                    modifiers: modifiers[toISODateString(day)],
                    ariaLabelFormat: dayAriaLabelFormat,
                  }),
                )}
              </CalendarWeek>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

CalendarMonth.propTypes = propTypes;
CalendarMonth.defaultProps = defaultProps;

const mapStateToProps = state => {
  return {
    i18n: state.i18n,
  };
};

export default connect(
  mapStateToProps,
  {},
)(
  withStyles(
    ({ reactDates: { color, font, spacing } }) => ({
      CalendarMonth: {
        background: color.background,
        textAlign: 'center',
        verticalAlign: 'top',
        userSelect: 'none',
      },

      CalendarMonth_table: {
        borderCollapse: 'collapse',
        borderSpacing: 0,
      },

      CalendarMonth_verticalSpacing: {
        borderCollapse: 'separate',
      },

      CalendarMonth_caption: {
        color: color.text,
        fontSize: font.captionSize,
        textAlign: 'center',
        paddingTop: spacing.captionPaddingTop,
        paddingBottom: spacing.captionPaddingBottom,
        captionSide: 'initial',
      },

      CalendarMonth_caption__verticalScrollable: {
        paddingTop: 12,
        paddingBottom: 7,
      },
    }),
    { pureComponent: typeof React.PureComponent !== 'undefined' },
  )(CalendarMonth),
);
